import type { RenderContext, VideoFrame } from '../types';
import { WebGLShaderProgram, drawFullscreenQuad } from '../gpu/shader';
import { uploadCanvasToTexture } from '../gpu/texturePool';
import { BaseEffect } from './base';
import { VERTEX_FULLSCREEN, FRAG_MOTION_BLUR } from '../shaders/glsl';

const DEFAULTS = { strength: 0.5, sampleCount: 8 };

let motionProg: WebGLShaderProgram | null = null;

export class MotionBlurEffect extends BaseEffect {
  constructor(id: string, clip: Partial<import('../types').EffectTimelineClip>) {
    super(id, 'motion_blur', clip, DEFAULTS);
  }

  protected renderGpu(frame: VideoFrame, params: Record<string, number>, context: RenderContext): VideoFrame {
    const prev = context.previousFrames[0];
    if (!prev) return frame;
    const gpu = context.gpu!;
    const gl = gpu.gl;
    if (!motionProg) motionProg = new WebGLShaderProgram(gl, VERTEX_FULLSCREEN, FRAG_MOTION_BLUR);
    const pool = gpu.texturePool;
    const curTex = pool.acquire(frame.width, frame.height);
    const prevTex = pool.acquire(frame.width, frame.height);
    const outTex = pool.acquire(frame.width, frame.height);
    uploadCanvasToTexture(gl, curTex, frame.canvas);
    uploadCanvasToTexture(gl, prevTex, prev.canvas);
    const fb = gl.createFramebuffer();
    gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, outTex, 0);
    gl.viewport(0, 0, frame.width, frame.height);
    motionProg.use();
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, curTex);
    motionProg.setUniform1i('u_current', 0);
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, prevTex);
    motionProg.setUniform1i('u_prev', 1);
    motionProg.setUniform1f('u_strength', params.strength);
    motionProg.setUniform1i('u_sampleCount', Math.round(params.sampleCount));
    drawFullscreenQuad(gl, motionProg);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.deleteFramebuffer(fb);
    const result = this.textureToCanvas(gl, outTex, frame.width, frame.height);
    pool.release(curTex);
    pool.release(prevTex);
    pool.release(outTex);
    return result;
  }

  private textureToCanvas(gl: WebGL2RenderingContext, tex: WebGLTexture, w: number, h: number): VideoFrame {
    const fb = gl.createFramebuffer();
    gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
    const pixels = new Uint8Array(w * h * 4);
    gl.readPixels(0, 0, w, h, gl.RGBA, gl.UNSIGNED_BYTE, pixels);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.deleteFramebuffer(fb);
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      const img = ctx.createImageData(w, h);
      for (let y = 0; y < h; y += 1) {
        const srcRow = (h - 1 - y) * w * 4;
        img.data.set(pixels.subarray(srcRow, srcRow + w * 4), y * w * 4);
      }
      ctx.putImageData(img, 0, 0);
    }
    return { width: w, height: h, canvas, texture: null };
  }

  protected renderCpu(frame: VideoFrame, params: Record<string, number>, context: RenderContext): VideoFrame {
    const prev = context.previousFrames[0];
    if (!prev) return frame;
    const canvas = document.createElement('canvas');
    canvas.width = frame.width;
    canvas.height = frame.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return frame;
    const samples = Math.max(2, Math.round(params.sampleCount));
    const alpha = 1 / samples;
    for (let i = samples - 1; i >= 0; i -= 1) {
      const t = i / samples;
      const offX = t * params.strength * frame.width * 0.02;
      ctx.globalAlpha = alpha * (samples - i);
      ctx.drawImage(prev.canvas as CanvasImageSource, -offX, 0);
    }
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'source-over';
    ctx.drawImage(frame.canvas as CanvasImageSource, 0, 0);
    return { width: frame.width, height: frame.height, canvas, texture: null };
  }
}
